// @ts-strict-ignore
import {Component, Injector, OnInit} from "@angular/core";
import {SimAudioChannelService} from "@services";
import {
	AudioDeviceInputChannelType,
	AudioDeviceOutputChannelType,
	AudioInputChannel,
	AudioOutputChannel,
	NetworkAudioInputChannel,
	PhoneAudioInputChannel,
	RecorderMapping,
} from "@models";
import {
	BasicMaterialDialog,
	MatDialogDataBaseInput,
	MatDialogDataBaseOutput,
} from "../../basic-material-dialog/basic-material-dialog.directive";
import {Translations} from "@translations";
import {FormControl} from "@angular/forms";

export interface RECORDER_AUDIO_CHANNEL_SELECTION_INPUT extends MatDialogDataBaseInput {
	recorderMapping: RecorderMapping;
}

export interface RECORDER_AUDIO_CHANNEL_SELECTION_OUTPUT extends MatDialogDataBaseOutput {
	updatedRecorderMapping: RecorderMapping;
}

@Component({
	selector: "sim-recorder-audio-channel-selection-dialog",
	templateUrl: "./recorder-audio-channel-selection-dialog.component.html",
	styleUrls: ["./recorder-audio-channel-selection-dialog.component.scss"],
	standalone: false,
})
export class RecorderAudioChannelSelectionDialogComponent
	extends BasicMaterialDialog<
		RecorderAudioChannelSelectionDialogComponent,
		RECORDER_AUDIO_CHANNEL_SELECTION_INPUT,
		RECORDER_AUDIO_CHANNEL_SELECTION_OUTPUT
	>
	implements OnInit
{
	T = Translations;

	public vogInputChannelId: string = null;
	public vopInputChannelId: string = null;
	public vttInputChannelId: string = null;
	public phoneInputChannelId: string = null;
	public flyingInputChannelId: string = null;
	public vogNetworkInputChannelId: string = null;
	public vopNetworkInputChannelId: string = null;
	public vttNetworkInputChannelId: string = null;

	public vopOutputChannelId: string = null;
	public vttOutputChannelId: string = null;
	public directOutputChannelIdFC: FormControl<string> = new FormControl<string>(null);
	public websocketOutputChannelIdFC: FormControl<string> = new FormControl<string>(null);

	public availableVogAudioInputChannels: AudioInputChannel[] = [];
	public availableVopAudioInputChannels: AudioInputChannel[] = [];
	public availableVttAudioInputChannels: AudioInputChannel[] = [];
	public availablePhoneAudioInputChannels: PhoneAudioInputChannel[] = [];
	public availableFlyingAudioInputChannels: AudioInputChannel[] = [];
	public availableVogNetworkInputChannels: NetworkAudioInputChannel[] = [];
	public availableVopNetworkInputChannels: NetworkAudioInputChannel[] = [];
	public availableVttNetworkInputChannels: NetworkAudioInputChannel[] = [];

	public availableVopAudioOutputChannels: AudioOutputChannel[] = [];
	public availableVttAudioOutputChannels: AudioOutputChannel[] = [];
	public availableOutputChannels: AudioOutputChannel[] = [];
	public availableWebsocketOutputChannels: AudioOutputChannel[] = [];

	// phonechannels sind mit id, die anderen mit der channelnummer
	private audioInputChannelDictionary: {[key: string]: AudioInputChannel} = {};
	private phoneAudioInputChannelDictionary: {
		[key: string]: PhoneAudioInputChannel;
	} = {};
	private audioOutputChannelDictionary: {[key: string]: AudioOutputChannel} = {};
	private networkInputChannelDictionary: {
		[key: string]: NetworkAudioInputChannel;
	} = {};

	loadingAudioDeviceChannelsTask: Promise<unknown> = null;

	public filteredVogInputChannels: AudioInputChannel[] = null;
	public filteredVopInputChannels: AudioInputChannel[] = null;
	public filteredVttInputChannels: AudioInputChannel[] = null;
	public filteredPhoneInputChannels: PhoneAudioInputChannel[] = null;
	public filteredFlyingInputChannels: AudioInputChannel[] = null;
	public filteredDirectOutputChannels: AudioOutputChannel[] = null;
	public filteredVopOutputChannels: AudioOutputChannel[] = null;
	public filteredVttOutputChannels: AudioOutputChannel[] = null;
	public filteredVogNetworkInputChannels: NetworkAudioInputChannel[] = null;
	public filteredVopNetworkInputChannels: NetworkAudioInputChannel[] = null;
	public filteredVttNetworkInputChannels: NetworkAudioInputChannel[] = null;

	constructor(
		private _simAudioChannelService: SimAudioChannelService,
		_injector: Injector,
	) {
		super(_injector);
	}

	public ngOnInit() {
		super.ngOnInit();

		this.init();
	}

	protected init() {
		if (this.data.recorderMapping) {
			this.data.recorderMapping.vogInputChannelIds = this.data.recorderMapping.vogInputChannelIds || [];
			this.data.recorderMapping.vopInputChannelIds = this.data.recorderMapping.vopInputChannelIds || [];
			this.data.recorderMapping.vttInputChannelIds = this.data.recorderMapping.vttInputChannelIds || [];
			this.data.recorderMapping.flyingInputchannelIds = this.data.recorderMapping.flyingInputchannelIds || [];

			this.data.recorderMapping.vopOutputChannels = this.data.recorderMapping.vopOutputChannels || [];

			this.data.recorderMapping.phoneInputChannelIds = this.data.recorderMapping.phoneInputChannelIds || [];

			this.directOutputChannelIdFC.setValue(this.data.recorderMapping.audioOutputChannelId);
			this.directOutputChannelIdFC.valueChanges.subscribe((value) => {
				this.data.recorderMapping.audioOutputChannelId = value;
			});

			this.websocketOutputChannelIdFC.setValue(this.data.recorderMapping.websocketAudioOutputChannelId);
			this.websocketOutputChannelIdFC.valueChanges.subscribe((value) => {
				this.data.recorderMapping.websocketAudioOutputChannelId = value;
			});
		}

		this.loadAudioDeviceChannels();
	}

	public saveRecorderMapping() {
		this.close({
			action: "ok",
			updatedRecorderMapping: this.data.recorderMapping,
		});
	}

	public loadAudioDeviceChannels() {
		this.audioInputChannelDictionary = {};
		this.phoneAudioInputChannelDictionary = {};
		this.audioOutputChannelDictionary = {};

		const inputChannelTask = this._simAudioChannelService.getAudioInputChannels().then((result) => {
			if (result) {
				if (result?.length > 0) {
					this.availableVogAudioInputChannels = [];
					this.availableVopAudioInputChannels = [];
					this.availableVttAudioInputChannels = [];
					this.availableFlyingAudioInputChannels = [];

					for (const audioInputChannel of result) {
						// create dictionary for all channels
						this.audioInputChannelDictionary[audioInputChannel.id] = audioInputChannel;

						// ------------------------ //
						// Audio channel types:     //
						// ------------------------ //
						// Default              = 0 //
						// VOG                  = 1 //
						// VTP                  = 2 //
						// DefaultOutputChannel = 3 //
						// VogOutputChannel     = 4 //
						// VopOutputChannel     = 5 //
						// VttInputChannel      = 7 //
						// FlyingMic            = 11//
						if (audioInputChannel.type === AudioDeviceInputChannelType.VOG) {
							this.availableVogAudioInputChannels.push(audioInputChannel);
						} else if (audioInputChannel.type === AudioDeviceInputChannelType.VTP) {
							this.availableVopAudioInputChannels.push(audioInputChannel);
						} else if (audioInputChannel.type === AudioDeviceInputChannelType.VttInputChannel) {
							this.availableVttAudioInputChannels.push(audioInputChannel);
						} else if (audioInputChannel.type === AudioDeviceInputChannelType.FlyingMic) {
							this.availableFlyingAudioInputChannels.push(audioInputChannel);
						}
					}
				}
				this.filteredVogInputChannels = null;
				this.filteredVopInputChannels = null;
				this.filteredVttInputChannels = null;
				this.filteredFlyingInputChannels = null;
			}
		});

		const outputChannelTask = this._simAudioChannelService.getAudioOutputChannels().then((result) => {
			if (result) {
				if (result?.length > 0) {
					this.availableOutputChannels = [];
					this.availableVopAudioOutputChannels = [];
					this.availableVttAudioOutputChannels = [];
					this.availableWebsocketOutputChannels = [];

					for (const audioOutputChannel of result) {
						// create dictionary for all channels
						this.audioOutputChannelDictionary[audioOutputChannel.id] = audioOutputChannel;

						// ------------------------ //
						// Audio channel types:     //
						// ------------------------ //
						// Default              = 0 //
						// VOG                  = 1 //
						// VTP                  = 2 //
						// DefaultOutputChannel = 3 //
						// VogOutputChannel     = 4 //
						// VopOutputChannel     = 5 //
						// VttOutputChannel = 8
						if (audioOutputChannel.type === AudioDeviceOutputChannelType.DefaultOutputChannel) {
							this.availableOutputChannels.push(audioOutputChannel);
						} else if (audioOutputChannel.type === AudioDeviceOutputChannelType.VopOutputChannel) {
							this.availableVopAudioOutputChannels.push(audioOutputChannel);
						} else if (audioOutputChannel.type === AudioDeviceOutputChannelType.VttOutputChannel) {
							this.availableVttAudioOutputChannels.push(audioOutputChannel);
						} else if (audioOutputChannel.type == AudioDeviceOutputChannelType.Websocket) {
							this.availableWebsocketOutputChannels.push(audioOutputChannel);
						}
					}
				}
				this.filteredDirectOutputChannels = null;
				this.filteredVopOutputChannels = null;
				this.filteredVttOutputChannels = null;
			}
		});

		const phoneInputChannelTask = this._simAudioChannelService.getPhoneAudioInputChannels().then((result) => {
			if (result) {
				if (result?.length > 0) {
					this.availablePhoneAudioInputChannels = [];

					for (const phoneAudioInputChannel of result) {
						// create dictionary for all channels
						this.phoneAudioInputChannelDictionary[phoneAudioInputChannel.id] = phoneAudioInputChannel;

						// ------------------------ //
						// Audio channel types:     //
						// ------------------------ //
						// PhoneInputChannel     = 9 //
						if (phoneAudioInputChannel.type === AudioDeviceInputChannelType.PhoneInputChannel) {
							this.availablePhoneAudioInputChannels.push(phoneAudioInputChannel);
						}
					}
				}

				this.filteredPhoneInputChannels = null;
			}
		});

		const networkInputChannelTask = this._simAudioChannelService.getFilteredNetworkAudioInputChannels().then((result) => {
			if (result) {
				if (result.data?.length > 0) {
					this.availableVogNetworkInputChannels = [];
					this.availableVopNetworkInputChannels = [];
					this.availableVttNetworkInputChannels = [];
					for (const networkInputChannel of result.data) {
						// create dictionary for all channels
						this.networkInputChannelDictionary[networkInputChannel.id] = networkInputChannel;

						if (networkInputChannel.type === AudioDeviceInputChannelType.VOG) {
							this.availableVogNetworkInputChannels.push(networkInputChannel);
						} else if (networkInputChannel.type === AudioDeviceInputChannelType.VTP) {
							this.availableVopNetworkInputChannels.push(networkInputChannel);
						} else if (networkInputChannel.type === AudioDeviceInputChannelType.VttInputChannel) {
							this.availableVttNetworkInputChannels.push(networkInputChannel);
						}
					}
				}

				this.filteredVogNetworkInputChannels = null;
				this.filteredVopNetworkInputChannels = null;
				this.filteredVttNetworkInputChannels = null;
			}
		});
		this.loadingAudioDeviceChannelsTask = Promise.all([
			inputChannelTask,
			outputChannelTask,
			phoneInputChannelTask,
			networkInputChannelTask,
		]);
	}

	public getInputChannelName(audioInputChannelId: string) {
		if (this.audioInputChannelDictionary[audioInputChannelId]) {
			return this.audioInputChannelDictionary[audioInputChannelId].displayName;
		} else {
			return `Channel not defined: ${audioInputChannelId}`;
		}
	}

	public getPhoneInputChannelName(channelId: string) {
		if (this.phoneAudioInputChannelDictionary[channelId]) {
			return this.phoneAudioInputChannelDictionary[channelId].displayName;
		} else {
			return `Channel not defined with id : ${channelId}`;
		}
	}

	public getOutputChannelName(channelId: string) {
		if (this.audioOutputChannelDictionary[channelId]) {
			return this.audioOutputChannelDictionary[channelId].displayName;
		} else {
			return `Channel not defined: ${channelId}`;
		}
	}

	public getNetworkChannelName(channelId: string) {
		if (this.networkInputChannelDictionary[channelId]) {
			return this.networkInputChannelDictionary[channelId].displayName;
		} else {
			return `Channel not defined: ${channelId}`;
		}
	}

	public addChannel(channelId: string, channelIds: string[]) {
		if (channelIds.indexOf(channelId) < 0) {
			channelIds.push(channelId);
		}
		this.resetFilters();
	}

	public addPhoneInputChannel(channelId: string, channelIds: string[]) {
		if (channelId && channelIds.indexOf(channelId) < 0 && this.data.recorderMapping.phoneInputChannelIds.length === 0) {
			channelIds.push(channelId);
		}
		this.resetFilters();
	}

	public removeChannel(channelId: string, channelIds: string[]) {
		channelIds.splice(channelIds.indexOf(channelId), 1);
		this.resetFilters();
	}

	public filterVogInputChannels() {
		this.data.recorderMapping.vogInputChannelIds ??= [];
		if (!!this.availableVogAudioInputChannels) {
			this.filteredVogInputChannels = this.availableVogAudioInputChannels.filter(
				(audioChannel) => !this.data.recorderMapping.vogInputChannelIds.includes(audioChannel.id),
			);
		}
		this.vogInputChannelId = null;

		return this.filteredVogInputChannels;
	}

	public filterVopInputChannels() {
		this.data.recorderMapping.vopInputChannelIds ??= [];
		if (!!this.availableVopAudioInputChannels) {
			this.filteredVopInputChannels = this.availableVopAudioInputChannels.filter(
				(audioChannel) => !this.data.recorderMapping.vopInputChannelIds.includes(audioChannel.id),
			);
		}
		this.vopInputChannelId = null;

		return this.filteredVopInputChannels;
	}

	public filterVttInputChannels() {
		this.data.recorderMapping.vttInputChannelIds ??= [];
		if (!!this.availableVttAudioInputChannels) {
			this.filteredVttInputChannels = this.availableVttAudioInputChannels.filter(
				(audioChannel) => !this.data.recorderMapping.vttInputChannelIds.includes(audioChannel.id),
			);
		}
		this.vttInputChannelId = null;

		return this.filteredVttInputChannels;
	}

	public filterPhoneInputChannels() {
		this.data.recorderMapping.phoneInputChannelIds ??= [];
		if (!!this.availablePhoneAudioInputChannels) {
			this.filteredPhoneInputChannels = this.availablePhoneAudioInputChannels.filter(
				(audioChannel) => !this.data.recorderMapping.phoneInputChannelIds.includes(audioChannel.id),
			);
		}
		this.phoneInputChannelId = null;

		return this.filteredPhoneInputChannels;
	}

	public filterFlyingInputChannels() {
		this.data.recorderMapping.flyingInputchannelIds ??= [];
		if (!!this.availableFlyingAudioInputChannels) {
			this.filteredFlyingInputChannels = this.availableFlyingAudioInputChannels.filter(
				(audioChannel) => !this.data.recorderMapping.flyingInputchannelIds.includes(audioChannel.id),
			);
		}
		this.flyingInputChannelId = null;

		return this.filteredFlyingInputChannels;
	}

	public filterVogNetworkInputChannels() {
		this.data.recorderMapping.vogNetworkAudioInputChannelIds ??= [];
		if (!!this.availableVogNetworkInputChannels) {
			this.filteredVogNetworkInputChannels = this.availableVogNetworkInputChannels.filter(
				(audioChannel) => !this.data.recorderMapping.vogNetworkAudioInputChannelIds.includes(audioChannel.id),
			);
		}
		this.vogNetworkInputChannelId = null;

		return this.filteredVogNetworkInputChannels;
	}

	public filterVopNetworkInputChannels() {
		this.data.recorderMapping.vopNetworkAudioInputChannelIds ??= [];
		if (!!this.availableVopNetworkInputChannels) {
			this.filteredVopNetworkInputChannels = this.availableVopNetworkInputChannels.filter(
				(audioChannel) => !this.data.recorderMapping.vopNetworkAudioInputChannelIds.includes(audioChannel.id),
			);
		}
		this.vopNetworkInputChannelId = null;

		return this.filteredVopNetworkInputChannels;
	}

	public filterVttNetworkInputChannels() {
		this.data.recorderMapping.vttNetworkAudioInputChannelIds ??= [];
		if (!!this.availableVttNetworkInputChannels) {
			this.filteredVttNetworkInputChannels = this.availableVttNetworkInputChannels.filter(
				(audioChannel) => !this.data.recorderMapping.vttNetworkAudioInputChannelIds.includes(audioChannel.id),
			);
		}
		this.vttNetworkInputChannelId = null;

		return this.filteredVttNetworkInputChannels;
	}

	public filterVopOutputChannels() {
		this.data.recorderMapping.vopOutputChannelIds ??= [];
		if (!!this.availableVopAudioOutputChannels) {
			this.filteredVopOutputChannels = this.availableVopAudioOutputChannels.filter(
				(audioChannel) => !this.data.recorderMapping.vopOutputChannelIds.includes(audioChannel.id),
			);
		}
		this.vopOutputChannelId = null;

		return this.filteredVopOutputChannels;
	}

	public filterVttOutputChannels() {
		this.data.recorderMapping.vttOutputChannelIds ??= [];
		if (!!this.availableVttAudioOutputChannels) {
			this.filteredVttOutputChannels = this.availableVttAudioOutputChannels.filter(
				(audioChannel) => !this.data.recorderMapping.vttOutputChannelIds.includes(audioChannel.id),
			);
		}
		this.vttOutputChannelId = null;

		return this.filteredVttOutputChannels;
	}

	private resetFilters() {
		this.filteredVogInputChannels = null;
		this.filteredVopInputChannels = null;
		this.filteredVttInputChannels = null;
		this.filteredPhoneInputChannels = null;
		this.filteredFlyingInputChannels = null;
		this.filteredDirectOutputChannels = null;
		this.filteredVopOutputChannels = null;
		this.filteredVttOutputChannels = null;
		this.filteredVogNetworkInputChannels = null;
		this.filteredVopNetworkInputChannels = null;
	}
}
